// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
import Vue from 'vue'
import App from './App'
import router from './router'
import Vuetify from 'vuetify'
import 'vuetify/dist/vuetify.min.css'
import axios from 'axios'
import VueAxios from 'vue-axios'

// Set up Axios for API requests
Vue.use(VueAxios, axios)
switch (process.env.NODE_ENV) {
  case 'development':
    Vue.axios.defaults.baseURL = 'https://api.local.com'
    break
  case 'testing':
    // If we are running a server for tests, we want to make sure the API knows it
    Vue.axios.defaults.headers.common['X-SYNC-Test'] = true
    Vue.axios.defaults.baseURL = 'https://172.20.2.101'
    break
  default:
    Vue.axios.defaults.baseURL = 'https://api.anewfocus.org'
}

// Set up necessary components for vue-auth
Vue.router = router
App.router = router
Vue.use(require('@websanova/vue-auth'), {
  auth: require('@websanova/vue-auth/drivers/auth/bearer.js'),
  http: require('@websanova/vue-auth/drivers/http/axios.1.x.js'),
  router: require('@websanova/vue-auth/drivers/router/vue-router.2.x.js'),
  // We handle our token expiration logic through our 401 intercept below, so
  // we can override the default tokenExpired check to always return false
  tokenExpired: function () { return false },
  // Configure our token refresh to occur automatically every 5 minutes. Certain
  // network requests will also cause a refresh to happen through a 401 response
  // but this setting guarantees that the service worker's caching rules won't
  // ever keep us in a pseudo-logged-in state any longer than 5 minutes.
  refreshData: { enabled: true, interval: 5 },
  requestIntercept: function (req) {
    var token
    var tokenName = this.options.tokenDefaultName

    // We use the ID token if this is a refresh request, otherwise we will use
    // the access token (default name)
    if (req.url === this.options.refreshData.url) {
      tokenName = 'id'
    }
    token = this.token(tokenName)

    if (token) {
      this.options.auth.request.call(this, req, token)
    }

    return req
  },
  responseIntercept: function (res/*, req */) {
    var token
    var path = res.config.url.substring(res.config.baseURL.length + 1)

    // We treat 401 (unauthenticated) responses specially
    if (res.status === 401) {
      // If the request is for our refresh endpoint, just make sure we're logged
      // out locally since we're apparently logged out remotely
      if (path === this.options.refreshData.url) {
        this.flashMessage = 'Session expired. Please log in again.'
        return this.logout({ makeRequest: false, redirect: '/login' })
      }

      // If the request wasn't for our refresh endpoint, try to refresh our
      // access token and send the request again
      // (unless the request was specifically configured to ignore this 401 check)
      var _config = res.config
      if (_config.data) {
        let d = JSON.parse(_config.data)
        if (d.ignore401 && d.ignore401 === true) {
          return
        }
      }
      var _this = this
      return this.refresh({
        success: function () {
          // The refresh succeeded, which means we now have a good access token
          // Try resending the request, minus the original authorization header
          delete _config.headers.Authorization
          _this.options.http._http.call(_this, _config)
        },
        error: function () {
          // The refresh failed, which means that not only was our access token
          // invalid (the original 401), our ID token is also invalid. To make
          // sure our state is accurate, we request a local logout and prompt
          // the user to log in again.
          _this.logout({ makeRequest: false, redirect: '/login' })
        }
      }) // eslint-disable-next-line
    }
    // We treat 402 (payment required) responses specially
    else if (res.status === 402) {
      // If an API request returns 402, it means that the user doesn't have an
      // active subscription as required by that request. We direct them to
      // another page with information about subscribing unless the request was
      // specifically asking about subscription status, in which case we assume
      // that the requesting method will handle any necessary redirection.
      if (path !== 'status/subscription') {
        return this.options.Vue.router.push('/subscribe')
      }
      // eslint-disable-next-line
    }
    // We treat 500+ (server error) responses specially
    else if (res.status >= 500) {
      // Since we're not responsible for whatever caused this response, we want
      // to retry our request again (up to MAX_RETRIES number of times)
      const MAX_RETRIES = 3

      // We don't want to retry file uploads since they're more complicated and
      // resource intensive
      if (path.startsWith('files/uploads')) {
        return res
      }

      // Make sure we haven't already exceeded our limit of retries
      var retries = 0
      let d = res.config.data
      if (typeof d._retries !== 'undefined') {
        retries = d._retries
      }
      if (retries < MAX_RETRIES) {
        d._retries = retries + 1
        res.config.data = JSON.stringify(d)
        return this.options.http._http.call(this, res.config)
      }
    }

    // Let the auth driver handle token extraction if appropriate
    token = this.options.auth.response.call(this, res)

    // Save the token if we have it
    if (token) {
      this.token(null, token)
    }

    return res
  },
  checkAuthenticated: function (cb) {
    // We require a custom authentication check because the standard one
    // honors the rememberMe cookie value to decide whether or not to keep the
    // user loggedin, but standalone PWAs on iOS clear cookies on launch.
    // This version mimics the logic of the standard method, just without the
    // cookie check (i.e., always assuming rememberMe == true).

    // If we don't already have a note saying whether or not we're authenticated
    // but we have a token stored, fetch user data to confirm our token is still good
    if (this.watch.authenticated === null &&
        this.token()) {
      // NOTE: The standard handler checks for the rememberMe cookie here and
      // makes a call to log the user out if no cookie exists.

      // Assume our check is going to fail, allowing the fetch to fix that assumption
      this.watch.authenticated = false

      if (this.options.fetchData.enabled) {
        this.options.fetchPerform.call(this, {
          success: cb,
          error: cb,
          enabled: true
        })
      } else {
        this.options.fetchProcess.call(this, {}, {})
        return cb.call(this)
      }
    } else {
      // If we made it here, it means we already had a value for whether or not
      // we're authenticated, so we can just use that and say we're done.
      this.watch.loaded = true
      return cb.call(this)
    }
  }
})

Vue.use(Vuetify, {
  theme: {
    primary: '#032f9e',
    secondary: '#ffffff',
    accent: '#ffffff'
  }
})

Vue.config.productionTip = false

/* eslint-disable no-new */
const v = new Vue(App).$mount('#app')

// Extend our vue-auth object to check for an active subscription
v.$auth.isSubscribed = function () {
  return axios.get('/status/subscription')
    .then(() => {
      // If we got a success response, then we know easily that we have an
      // active subscription. Send off a request for subscription details so
      // they'll be available in the future, then return our success.
      axios.get('/subscriptions').catch(() => {})
      return true
    })
    .catch(async (e) => {
      // If we got an error with a 402 status code, then we know easily that we
      // do not have an active subscription
      if (e && e.response && e.response.status && e.response.status === 402) {
        return false
      }

      // If we failed for another reason (e.g., network offline), then we return
      // the best answer we can based on our cached data
      let subs = (await this.subscriptions()).filter((sub) => {
        return sub.isActive
      })
      return (subs.length > 0) || (this.user().roles === 'admin')
    })
}

// Extend our vue-auth object to return a list of relevant subscriptions
v.$auth.subscriptions = async function () {
  try {
    let subs = (await axios.get('/subscriptions', { data: { ignore401: true } })).data
    let now = new Date()
    subs.forEach((sub) => {
      // Get more usable date values
      let props = [
        'created_at',
        'startDate',
        'endDate',
        'approvalDate',
        'cancelDate',
        'terminalDate'
      ]
      props.forEach((p) => {
        if (sub[p] !== null) {
          sub[p] = v.$root.parseApiDateTimeString(sub[p])
        }
      })

      // Set a "next billing cycle" value for convenience
      sub.nextCycle = (sub.terminalDate !== null && sub.endDate >= sub.terminalDate) ? null : sub.endDate

      // Determine if the subscription's dates are currently relevant
      if (now >= sub.startDate && now <= sub.endDate) {
        sub.isCurrent = true
      } else {
        sub.isCurrent = false
      }

      // Determine if the subscription is actually good for granting access
      sub.isActive = (
        sub.isCurrent &&
        (sub.hasActiveHold === false)
      )
    })

    // Order by descending terminal date (counting a null terminal date
    // essentially as positive infinity)
    subs.sort((a, b) => {
      if (a.terminalDate === null) {
        return -1
      }
      if (a.terminalDate > b.terminalDate) {
        return -1
      }
      if (b.terminalDate > a.terminalDate) {
        return 1
      }
      return 0
    })

    return subs
  } catch (e) {
    return []
  }
}

if ('serviceWorker' in navigator) {
  // Add a helper method for sending messages to the service worker
  v.swSendMessage = function (msg) {
    return new Promise(function (resolve, reject) {
      var chan = new MessageChannel()

      // Create a handler for the response message
      chan.port1.onmessage = function (ev) {
        if (ev.data.error) {
          reject(ev.data.error)
        } else {
          resolve(ev.data)
        }
      }

      // Send our message with a mechanism for reply
      navigator.serviceWorker.controller.postMessage(msg, [chan.port2])
    })
  }

  // Add a listener on our service worker so we can receive messages
  navigator.serviceWorker.addEventListener('message', function (ev) {
    switch (ev.data) {
      case 'OFFLINE':
        v.isOffline = true
        if (v.tryReconnectTimeoutId === null) {
          v.tryReconnectInterval = 3000
          v.tryReconnectTimeoutId = setTimeout(v.tryReconnect, v.tryReconnectInterval)
        }
        break
    }
  })
} else {
  // If we have no service worker, add a stub method to mimic our message helper
  v.swSendMessage = function () { return Promise.reject(new Error('Service Worker not available')) }
}

// Add a listener so we can prompt the user about installation if they're not
// already using it in standalone mode
v.pwaInstallationPrompt = null
if (!window.matchMedia('(display-mode: standalone)').matches &&
    window.navigator.standalone !== true) {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    v.pwaInstallationPrompt = e
  })
} else {
  v.pwaStandaloneMode = true
}

// Add a navigation guard to our router now that our Vue object is fully initialized
router.beforeResolve(async function (to, from, next) {
  // Set some rules for when there is a logged in user and the target route
  // has meta flags
  if (v.$auth.check() &&
      typeof to.meta !== 'undefined') {
    // Make sure the user has confirmed their email address if they're using
    // a local auth method. The user's profile will indicate if they have
    // confirmed an auth_local email.
    let u = v.$auth.user()
    if ((typeof to.meta.ignoreEmailConfirmation === 'undefined' || to.meta.ignoreEmailConfirmation !== true) &&
        u.local.email !== null && u.local.emailConfirmed === null) {
      return next('/confirm')
    }

    // Make sure the user has completed setup if that flag is set
    if (typeof to.meta.setup !== 'undefined' &&
        to.meta.setup === true) {
      // The test for a completed setup process is the presence of a user role
      if (!v.$auth.user().roles) {
        return next('/setup')
      }
    }

    // Make sure the user has an active subscription if that flag is set
    if (typeof to.meta.subscriptionRequired !== 'undefined' &&
        to.meta.subscriptionRequired === true) {
      if (await v.$auth.isSubscribed() !== true) {
        return next('/subscribe')
      }
    }
  }

  // If we make it this far without more specific requirements, just allow the route
  return next()
})
